/**
 * Editor de manuais (só `area_pcp:editar`, exige internet). Cria tarefa nova
 * ou edita uma existente: cabeçalho, identificador, seções e passos —
 * adicionar acima/abaixo, duplicar, remover (com desfazer), mover entre
 * seções, reordenar arrastando ou pelas setas, referenciar passos de outra
 * tarefa e pré-visualizar exatamente como no modo execução.
 *
 * Nada vai ao servidor até "Salvar versão" (Ctrl S): aí o manual inteiro vira
 * a versão seguinte. Se outra pessoa salvou antes, o servidor responde 409 e
 * a tela diz quem foi. Execuções abertas seguem na versão em que começaram.
 */
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { FiAlertTriangle, FiArrowLeft, FiCopy, FiEye, FiPlus, FiTrash2 } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import ConfirmDialog from "../../components/ConfirmDialog";
import { EmptyStateErro } from "../../components/EmptyState";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  apagarTarefa,
  criarTarefa,
  definirAtivo,
  lerTarefa,
  listarTarefas,
  salvarTarefa,
} from "../../services/areaPcp";
import { BASE } from "./Casca";
import ListaOrdenavel, { SetasDeOrdem } from "./componentes/ListaOrdenavel";
import PassoConteudo from "./componentes/PassoConteudo";
import {
  contarAlteracoes,
  editarRascunho,
  paraEnvio,
  passosComNumeroAMao,
  problemasDoRascunho,
  rascunhoDaTarefa,
  rascunhoVazio,
} from "./dominio/editor";
import EditorPasso from "./EditorPasso";
import { useAreaPcp } from "./offline/AreaPcpContext";
import {
  Aviso,
  BlocoTitulo,
  BotaoPerigo,
  BotaoPrimario,
  BotaoSecundario,
  BotaoTexto,
  Campo,
  Controles,
  Grade2,
  LinhaTitulo,
  Pagina,
  Secao,
  Segmentado,
  Selo,
  Topo,
  desktop,
} from "./styles";

const ABAS = [
  { id: "dados", rotulo: "Dados da tarefa" },
  { id: "passos", rotulo: "Passos" },
  { id: "fim", rotulo: "Entrega e erros" },
  { id: "identificador", rotulo: "Identificador" },
];

const Colunas = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;

  @media ${desktop} {
    grid-template-columns: minmax(0, 1fr) 380px;
  }
`;

const Previa = styled.aside`
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  padding: 18px;
  align-self: start;

  @media ${desktop} {
    position: sticky;
    top: calc(var(--app-header-height, 0px) + 110px);
  }

  .faixa {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--ink-3);
    margin-bottom: 10px;
  }
`;

const CaixaPasso = styled.div`
  border: 1px solid ${({ $aberto }) => ($aberto ? "var(--accent)" : "var(--border)")};
  border-radius: 12px;
  background: var(--surface);
  overflow: hidden;
  margin-bottom: 8px;
`;

const Resumo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 0;
  min-width: 0;

  .abrir {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    gap: 10px;
    align-items: baseline;
    border: 0;
    background: transparent;
    text-align: left;
    font: inherit;
    color: var(--ink);
    padding: 8px 0;
    cursor: pointer;
  }

  .numero {
    flex: 0 0 auto;
    width: 26px;
    text-align: right;
    font-weight: 700;
    color: var(--ink-3);
    font-variant-numeric: tabular-nums;
  }

  .texto {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 13.5px;
  }
`;

const AcoesDoPasso = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 12px 12px;
  background: var(--bg);
`;

function textoDoResumo(passo) {
  if (passo.titulo_curto) return passo.titulo_curto;
  if (passo.texto) return passo.texto.replace(/\*\*|`/g, "").slice(0, 90);
  if (passo.referencia) return "Sub-checklist de outra tarefa";
  return "(passo vazio)";
}

function useCarregarTarefa(id) {
  const { request } = useAuth();
  const [estado, setEstado] = useState({ carregando: Boolean(id), tarefa: null, erro: null });
  const recarregar = useCallback(() => {
    if (!id) return;
    setEstado((a) => ({ ...a, carregando: true }));
    lerTarefa(request, id)
      .then((tarefa) => setEstado({ carregando: false, tarefa, erro: null }))
      .catch((erro) => setEstado({ carregando: false, tarefa: null, erro }));
  }, [id, request]);
  useEffect(recarregar, [recarregar]);
  return { ...estado, recarregar, setTarefa: (tarefa) => setEstado({ carregando: false, tarefa, erro: null }) };
}

function useOutrasTarefas(id) {
  const { request } = useAuth();
  const [tarefas, setTarefas] = useState([]);
  useEffect(() => {
    listarTarefas(request).then((r) => setTarefas(r.tarefas.filter((t) => t.id !== id))).catch(() => setTarefas([]));
  }, [request, id]);
  return tarefas;
}

function CamposDeTexto({ rascunho, campos, despachar }) {
  return campos.map(([campo, rotulo, dica]) => (
    <Campo key={campo}>
      <span>
        {rotulo} {dica ? <span className="dica">{dica}</span> : null}
      </span>
      <textarea
        value={rascunho[campo]}
        maxLength={4000}
        onChange={(e) => despachar({ tipo: "campo", campo, valor: e.target.value })}
      />
    </Campo>
  ));
}

function Secoes({ rascunho, despachar }) {
  return (
    <Secao>
      <h2>Seções (sumário do manual)</h2>
      {rascunho.secoes.map((secao, indice) => (
        <div key={secao.chave} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ width: 22, color: "var(--ink-3)", fontWeight: 700, textAlign: "right" }}>{indice + 1}.</span>
          <Campo style={{ flex: 1 }}>
            <input
              aria-label={`Título da seção ${indice + 1}`}
              value={secao.titulo}
              maxLength={120}
              onChange={(e) => despachar({ tipo: "renomearSecao", chave: secao.chave, titulo: e.target.value })}
            />
          </Campo>
          <SetasDeOrdem
            indice={indice}
            total={rascunho.secoes.length}
            rotulo={secao.titulo || "seção"}
            onMover={(de, para) => despachar({ tipo: "moverSecao", de, para })}
          />
          <BotaoSecundario
            type="button"
            aria-label={`Apagar seção ${secao.titulo}`}
            style={{ width: 34, padding: 0 }}
            onClick={() => despachar({ tipo: "removerSecao", chave: secao.chave })}
          >
            <FiTrash2 size={15} aria-hidden="true" />
          </BotaoSecundario>
        </div>
      ))}
      <BotaoTexto type="button" style={{ alignSelf: "flex-start" }} onClick={() => despachar({ tipo: "adicionarSecao" })}>
        <FiPlus size={14} aria-hidden="true" /> Adicionar seção
      </BotaoTexto>
    </Secao>
  );
}

export default function Editor() {
  const { id } = useParams();
  const nova = !id;
  const navigate = useNavigate();
  const { request } = useAuth();
  const toast = useToast();
  const { carregarPacote, conexao } = useAreaPcp();
  const carga = useCarregarTarefa(id);
  const outrasTarefas = useOutrasTarefas(id);
  const ehDesktop = useMediaQuery(desktop);

  // Tarefa nova: original e rascunho partem do MESMO objeto, senão o passo
  // vazio de cada um nasceria com chave diferente e contaria como alteração.
  const [inicial] = useState(() => (nova ? rascunhoVazio() : null));
  const [original, setOriginal] = useState(inicial);
  const [rascunho, despachar] = useReducer(editarRascunho, inicial);
  const [aba, setAba] = useState(nova ? "dados" : "passos");
  const [aberto, setAberto] = useState(0);
  const [verPrevia, setVerPrevia] = useState(false);
  const [desfazer, setDesfazer] = useState(null);
  const [aRemover, setARemover] = useState(null);
  const [confirmarSaida, setConfirmarSaida] = useState(false);
  const [confirmarApagar, setConfirmarApagar] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!carga.tarefa) return;
    const base = rascunhoDaTarefa(carga.tarefa);
    setOriginal(base);
    despachar({ tipo: "restaurar", rascunho: base });
  }, [carga.tarefa]);

  const alteracoes = useMemo(() => (rascunho ? contarAlteracoes(original, rascunho) : 0), [original, rascunho]);
  const comNumeroAMao = useMemo(
    () => Object.fromEntries((rascunho ? passosComNumeroAMao(rascunho) : []).map((p) => [p.chave, p.achado])),
    [rascunho],
  );
  const sistemas = useMemo(
    () => [...new Set((rascunho?.passos || []).map((p) => p.sistema).filter(Boolean))].sort(),
    [rascunho],
  );

  const salvar = useCallback(async () => {
    const problemas = problemasDoRascunho(rascunho);
    if (problemas.length) {
      setErro(problemas.join(" "));
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      if (nova) {
        const criada = await criarTarefa(request, paraEnvio(rascunho));
        setOriginal(rascunho);
        toast.success("Tarefa criada como rascunho. Ative quando estiver pronta.");
        navigate(`${BASE}/tarefas/${criada.id}/editar`, { replace: true });
      } else {
        const salva = await salvarTarefa(request, id, paraEnvio(rascunho), carga.tarefa.versao_atual);
        carga.setTarefa(salva);
        toast.success(salva.versao_atual === carga.tarefa.versao_atual ? "Nada mudou" : `Versão ${salva.versao_atual} salva`);
      }
      carregarPacote();
    } catch (falha) {
      setErro(falha.message);
    } finally {
      setSalvando(false);
    }
  }, [rascunho, nova, request, id, carga, navigate, toast, carregarPacote]);

  useEffect(() => {
    const aoTeclar = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (alteracoes && !salvando) salvar();
      }
    };
    const aoSair = (e) => {
      if (!alteracoes) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("keydown", aoTeclar);
    window.addEventListener("beforeunload", aoSair);
    return () => {
      window.removeEventListener("keydown", aoTeclar);
      window.removeEventListener("beforeunload", aoSair);
    };
  }, [alteracoes, salvando, salvar]);

  if (carga.carregando || !rascunho || (!nova && !carga.tarefa && !carga.erro)) return <PageLoader scope="kpis" />;
  if (carga.erro) return <EmptyStateErro erro={carga.erro} titulo="Não foi possível abrir a tarefa para editar" onRetry={carga.recarregar} />;

  const tarefa = carga.tarefa;
  const destinoDeSaida = nova ? `${BASE}/tarefas` : `${BASE}/tarefas/${id}`;
  const sair = () => (alteracoes ? setConfirmarSaida(true) : navigate(destinoDeSaida));
  const passoAberto = rascunho.passos[aberto] || null;
  const abertas = tarefa?.execucoes_abertas;

  const removerPasso = (indice) => {
    setDesfazer({ rascunho, texto: `Passo ${indice + 1} removido` });
    despachar({ tipo: "removerPasso", indice });
    setAberto(Math.max(0, indice - 1));
    setARemover(null);
  };

  const previa = passoAberto ? (
    <Previa aria-label="Pré-visualização do passo">
      <div className="faixa">EXEMPLO - LOTE - L1 · Passo {aberto + 1} de {rascunho.passos.length}</div>
      <PassoConteudo passo={{ ...passoAberto, numero: aberto + 1 }} grande>
        {passoAberto.referencia ? <Aviso $tom="info">Na execução, este passo vira um sub-checklist da outra tarefa.</Aviso> : null}
      </PassoConteudo>
      <BotaoPrimario type="button" disabled style={{ width: "100%", marginTop: 16, minHeight: 48 }}>
        Feito
      </BotaoPrimario>
    </Previa>
  ) : null;

  return (
    <Pagina>
      <Topo>
        <LinhaTitulo>
          <BotaoSecundario type="button" aria-label="Voltar" onClick={sair} style={{ width: 34, padding: 0 }}>
            <FiArrowLeft size={16} aria-hidden="true" />
          </BotaoSecundario>
          <BlocoTitulo>
            <h1>{nova ? "Nova tarefa" : `Editar · ${tarefa.titulo}`}</h1>
            <p>
              {nova ? "Nasce como rascunho: só quem edita vê até você ativar." : `Versão ${tarefa.versao_atual}`}
              {alteracoes ? ` · ${alteracoes} ${alteracoes === 1 ? "alteração não salva" : "alterações não salvas"}` : ""}
            </p>
          </BlocoTitulo>
          <Controles>
            {alteracoes ? (
              <BotaoTexto type="button" onClick={() => despachar({ tipo: "restaurar", rascunho: original })}>
                Descartar
              </BotaoTexto>
            ) : null}
            <BotaoPrimario type="button" onClick={salvar} disabled={!alteracoes || salvando || conexao === "offline"} aria-keyshortcuts="Control+S">
              {salvando ? "Salvando…" : nova ? "Criar tarefa" : `Salvar versão ${tarefa.versao_atual + 1}`}
            </BotaoPrimario>
          </Controles>
        </LinhaTitulo>
        <Segmentado role="tablist" aria-label="Partes da tarefa">
          {ABAS.map((a) => (
            <button key={a.id} type="button" role="tab" aria-selected={aba === a.id} onClick={() => setAba(a.id)}>
              {a.rotulo}
              {a.id === "passos" ? ` · ${rascunho.passos.length}` : ""}
            </button>
          ))}
        </Segmentado>
      </Topo>

      {conexao === "offline" ? <Aviso $tom="warning">Sem conexão: editar manuais exige internet. O que você digitou fica na tela.</Aviso> : null}
      {erro ? (
        <Aviso $tom="danger" role="alert">
          <FiAlertTriangle size={16} aria-hidden="true" />
          <div>
            {erro}{" "}
            {/Recarregue/.test(erro) ? (
              <BotaoTexto type="button" onClick={() => { setErro(""); carga.recarregar(); }}>
                Recarregar (descarta o que não foi salvo)
              </BotaoTexto>
            ) : null}
          </div>
        </Aviso>
      ) : null}
      {abertas?.total ? (
        <Aviso $tom="info">
          {abertas.total === 1 ? "1 execução aberta usa" : `${abertas.total} execuções abertas usam`} esta tarefa
          {abertas.identificadores?.length ? ` (${abertas.identificadores.join(", ")})` : ""}. Elas continuam com a versão em que
          começaram; as próximas usam a nova.
        </Aviso>
      ) : null}
      {tarefa && !nova ? (
        <Aviso $tom={tarefa.ativo ? "success" : "neutral"}>
          <div style={{ flex: 1 }}>
            {tarefa.ativo ? "Ativa: a equipe vê e executa esta tarefa." : "Rascunho: só quem edita vê esta tarefa."}
          </div>
          <BotaoSecundario
            type="button"
            disabled={Boolean(alteracoes)}
            title={alteracoes ? "Salve as alterações antes" : undefined}
            onClick={async () => {
              try {
                carga.setTarefa(await definirAtivo(request, id, !tarefa.ativo));
                carregarPacote();
              } catch (falha) {
                setErro(falha.message);
              }
            }}
          >
            {tarefa.ativo ? "Voltar a rascunho" : "Ativar para a equipe"}
          </BotaoSecundario>
        </Aviso>
      ) : null}

      {aba === "dados" ? (
        <Secao>
          <Campo>
            <span>Título da tarefa *</span>
            <input value={rascunho.titulo} maxLength={120} onChange={(e) => despachar({ tipo: "campo", campo: "titulo", valor: e.target.value })} />
          </Campo>
          <CamposDeTexto
            rascunho={rascunho}
            despachar={despachar}
            campos={[
              ["objetivo", "Objetivo"],
              ["quando", "Quando"],
              ["envolvidos", "Envolvidos"],
              ["sistemas", "Sistemas"],
              ["regras", "Regras", "(use “- ” no começo da linha para lista)"],
            ]}
          />
        </Secao>
      ) : null}

      {aba === "fim" ? (
        <Secao>
          <CamposDeTexto
            rascunho={rascunho}
            despachar={despachar}
            campos={[
              ["entrega_final", "Entrega final"],
              ["erros_comuns", "Erros comuns"],
              ["em_aberto", "Pontos em aberto"],
            ]}
          />
        </Secao>
      ) : null}

      {aba === "identificador" ? (
        <Secao>
          <Aviso $tom="info">
            O identificador aparece no topo de cada execução (ex.: “Lote 522 — Passo 14 de 36”) e impede abrir duas
            vezes o mesmo lote ao mesmo tempo.
          </Aviso>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
            <input
              type="checkbox"
              checked={rascunho.exige_identificador}
              onChange={(e) => despachar({ tipo: "campo", campo: "exige_identificador", valor: e.target.checked })}
            />
            Obrigatório para começar (ex.: otimizar ou desfazer lote)
          </label>
          <Grade2>
            <Campo>
              <span>Rótulo</span>
              <input value={rascunho.rotulo_identificador} maxLength={60} placeholder="Nome do lote" onChange={(e) => despachar({ tipo: "campo", campo: "rotulo_identificador", valor: e.target.value })} />
            </Campo>
            <Campo>
              <span>Exemplo</span>
              <input value={rascunho.exemplo_identificador} maxLength={120} placeholder="522 - FELIPE PROCÓPIO - L2" onChange={(e) => despachar({ tipo: "campo", campo: "exemplo_identificador", valor: e.target.value })} />
            </Campo>
          </Grade2>
          <Campo>
            <span>Formato (dica para quem digita)</span>
            <input value={rascunho.formato_identificador} maxLength={120} placeholder="NÚMERO - NOME DO CLIENTE - L(sequência)" onChange={(e) => despachar({ tipo: "campo", campo: "formato_identificador", valor: e.target.value })} />
          </Campo>
        </Secao>
      ) : null}

      {aba === "passos" ? (
        <Colunas>
          <div>
            {!ehDesktop ? (
              <Segmentado role="group" aria-label="Ver" style={{ marginBottom: 10 }}>
                <button type="button" aria-pressed={!verPrevia} onClick={() => setVerPrevia(false)}>Editar</button>
                <button type="button" aria-pressed={verPrevia} onClick={() => setVerPrevia(true)}>
                  <FiEye size={13} aria-hidden="true" /> Pré-visualizar
                </button>
              </Segmentado>
            ) : null}
            {desfazer ? (
              <Aviso $tom="neutral" style={{ marginBottom: 8 }}>
                <span style={{ flex: 1 }}>{desfazer.texto}.</span>
                <BotaoTexto type="button" onClick={() => { despachar({ tipo: "restaurar", rascunho: desfazer.rascunho }); setDesfazer(null); }}>
                  Desfazer
                </BotaoTexto>
              </Aviso>
            ) : null}
            {!ehDesktop && verPrevia ? previa : (
              <>
                <ListaOrdenavel
                  itens={rascunho.passos}
                  chave={(p) => p.chave}
                  rotuloDoItem={(p, i) => `passo ${i + 1}`}
                  onMover={(de, para) => {
                    despachar({ tipo: "moverPasso", de, para });
                    if (aberto === de) setAberto(para);
                  }}
                  renderItem={(passo, indice, alca) => (
                    <CaixaPasso $aberto={indice === aberto}>
                      <Resumo>
                        {alca}
                        <button type="button" className="abrir" aria-expanded={indice === aberto} onClick={() => setAberto(indice === aberto ? -1 : indice)}>
                          <span className="numero">{indice + 1}</span>
                          <span className="texto">{textoDoResumo(passo)}</span>
                        </button>
                        {passo.aviso ? <Selo $tom="warning">aviso</Selo> : null}
                        {passo.referencia ? <Selo $tom="info">sub-checklist</Selo> : null}
                        {comNumeroAMao[passo.chave] ? <Selo $tom="warning">nº à mão</Selo> : null}
                        <SetasDeOrdem
                          indice={indice}
                          total={rascunho.passos.length}
                          rotulo={`passo ${indice + 1}`}
                          onMover={(de, para) => {
                            despachar({ tipo: "moverPasso", de, para });
                            if (aberto === de) setAberto(para);
                          }}
                        />
                      </Resumo>
                      {indice === aberto ? (
                        <>
                          <EditorPasso
                            passo={passo}
                            numero={indice + 1}
                            secoes={rascunho.secoes}
                            sistemas={sistemas}
                            outrasTarefas={outrasTarefas}
                            alertaDeNumero={comNumeroAMao[passo.chave]}
                            onCampo={(campo, valor) => despachar({ tipo: "editarPasso", indice, campo, valor })}
                          />
                          <AcoesDoPasso>
                            <BotaoSecundario type="button" onClick={() => { despachar({ tipo: "adicionarPasso", indice, onde: "acima" }); setAberto(indice); }}>
                              <FiPlus size={14} aria-hidden="true" /> Acima
                            </BotaoSecundario>
                            <BotaoSecundario type="button" onClick={() => { despachar({ tipo: "adicionarPasso", indice, onde: "abaixo" }); setAberto(indice + 1); }}>
                              <FiPlus size={14} aria-hidden="true" /> Abaixo
                            </BotaoSecundario>
                            <BotaoSecundario type="button" onClick={() => { despachar({ tipo: "duplicarPasso", indice }); setAberto(indice + 1); }}>
                              <FiCopy size={14} aria-hidden="true" /> Duplicar
                            </BotaoSecundario>
                            <BotaoPerigo type="button" onClick={() => setARemover(indice)}>
                              <FiTrash2 size={14} aria-hidden="true" /> Remover
                            </BotaoPerigo>
                          </AcoesDoPasso>
                        </>
                      ) : null}
                    </CaixaPasso>
                  )}
                />
                <BotaoSecundario
                  type="button"
                  onClick={() => {
                    despachar({ tipo: "adicionarPasso", indice: rascunho.passos.length - 1, onde: "abaixo" });
                    setAberto(rascunho.passos.length);
                  }}
                >
                  <FiPlus size={15} aria-hidden="true" /> Adicionar passo no fim
                </BotaoSecundario>
                <div style={{ marginTop: 20 }}>
                  <Secoes rascunho={rascunho} despachar={despachar} />
                </div>
              </>
            )}
          </div>
          {ehDesktop ? previa : null}
        </Colunas>
      ) : null}

      {!nova ? (
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16, marginTop: 8 }}>
          <BotaoPerigo type="button" onClick={() => setConfirmarApagar(true)}>
            <FiTrash2 size={14} aria-hidden="true" /> Apagar tarefa
          </BotaoPerigo>
        </div>
      ) : null}

      <ConfirmDialog
        open={aRemover !== null}
        titulo={`Remover o passo ${aRemover + 1}?`}
        descricao={`${aRemover !== null ? textoDoResumo(rascunho.passos[aRemover] || {}) : ""}. Os passos seguintes sobem um número. Você ainda pode desfazer ou descartar antes de salvar, ou restaurar pelo histórico depois.`}
        confirmarLabel="Remover passo"
        onConfirmar={() => removerPasso(aRemover)}
        onCancelar={() => setARemover(null)}
      />
      <ConfirmDialog
        open={confirmarSaida}
        titulo="Sair sem salvar?"
        descricao={`Você tem ${alteracoes} ${alteracoes === 1 ? "alteração não salva" : "alterações não salvas"}. Elas serão perdidas.`}
        confirmarLabel="Sair sem salvar"
        onConfirmar={() => navigate(destinoDeSaida)}
        onCancelar={() => setConfirmarSaida(false)}
      />
      <ConfirmDialog
        open={confirmarApagar}
        titulo={`Apagar "${tarefa?.titulo}"?`}
        descricao="A tarefa sai da lista de todos e vai para a lixeira (Tarefas › Apagadas), de onde pode ser restaurada. O histórico das execuções continua."
        confirmarLabel="Apagar tarefa"
        onConfirmar={async () => {
          try {
            await apagarTarefa(request, id);
            toast.success("Tarefa enviada para a lixeira");
            carregarPacote();
            navigate(`${BASE}/tarefas`);
          } catch (falha) {
            setConfirmarApagar(false);
            setErro(falha.message);
          }
        }}
        onCancelar={() => setConfirmarApagar(false)}
      />
    </Pagina>
  );
}
